import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import AddTaskDialog from '../components/AddTaskDialog';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { 
    CalendarPlus, 
    Calendar as CalendarIcon, 
    CheckSquare, 
    AlertTriangle, 
    Users,
    Plus,
    Clock,
    ArrowRight,
    History,
    RotateCcw,
    Check,
    PhoneCall,
    UserX,
    BarChart3,
    Search as SearchIcon,
    PhoneMissed,
    Truck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        visites_planifiees: 0,
        taches_en_cours: 0,
        problemes_a_regler: 0,
        visites_programmees: 0
    });
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [taskHistory, setTaskHistory] = useState([]);
    const [addTaskOpen, setAddTaskOpen] = useState(false);
    const [showTaskHistory, setShowTaskHistory] = useState(false);
    const [clientsRelancer, setClientsRelancer] = useState([]);
    const [urgentCount, setUrgentCount] = useState(0);
    const [devisNonEnvoyesCount, setDevisNonEnvoyesCount] = useState(0);
    const [prospectsSansReponseCount, setProspectsSansReponseCount] = useState(0);
    const [missedCallsCount, setMissedCallsCount] = useState(0);
    const [clientsLivraison, setClientsLivraison] = useState([]);
    const [quickSearch, setQuickSearch] = useState('');

    const canSeeTasks = ['admin', 'assistante', 'responsable', 'commercial'].includes(user?.role);

    const submitQuickSearch = (e) => {
        e?.preventDefault?.();
        const q = quickSearch.trim();
        if (q) navigate(`/recherche?q=${encodeURIComponent(q)}`);
        else navigate('/recherche');
    };

    useEffect(() => {
        fetchStats();
        if (canSeeTasks) fetchActiveTasks();
        fetchClientsRelancer();
        fetchUrgentCount();
        fetchDevisNonEnvoyesCount();
        fetchProspectsSansReponseCount();
        fetchMissedCallsCount();
        fetchClientsLivraison();
    }, []);

    const fetchClientsLivraison = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/dashboard/clients-a-livrer`, { withCredentials: true });
            setClientsLivraison(Array.isArray(res.data) ? res.data : []);
        } catch (err) { console.error(err); }
    };

    const fetchMissedCallsCount = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/calls/missed-today`, { withCredentials: true });
            setMissedCallsCount(res.data?.count || 0);
        } catch { /* OVH may not be configured — silently ignore */ }
    };

    const fetchDevisNonEnvoyesCount = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/dashboard/devis-non-envoyes`, { withCredentials: true });
            setDevisNonEnvoyesCount(Array.isArray(res.data) ? res.data.length : 0);
        } catch (err) { console.error(err); }
    };

    const fetchProspectsSansReponseCount = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/dashboard/prospects-sans-reponse`, { withCredentials: true });
            setProspectsSansReponseCount(Array.isArray(res.data) ? res.data.length : 0);
        } catch (err) { console.error(err); }
    };

    const fetchActiveTasks = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/tasks?status=active`, { withCredentials: true });
            setTasks(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchTaskHistory = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/tasks?status=completed`, { withCredentials: true });
            setTaskHistory(res.data);
            setShowTaskHistory(true);
        } catch (err) { console.error(err); }
    };

    const fetchClientsRelancer = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/dashboard/clients-relancer`, { withCredentials: true });
            setClientsRelancer(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchUrgentCount = async () => {
        try {
            const [aotRes, gmRes, mmRes, clientsRes, rdvRes] = await Promise.all([
                axios.get(`${API_URL}/api/aot-list`, { withCredentials: true }),
                axios.get(`${API_URL}/api/garde-meuble-list`, { withCredentials: true }),
                axios.get(`${API_URL}/api/monte-meuble-list`, { withCredentials: true }),
                axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
                axios.get(`${API_URL}/api/dashboard/rdv-non-affectes`, { withCredentials: true }).catch(() => ({ data: [] })),
            ]);
            let count = aotRes.data.filter(i => i.is_urgent).length;
            count += mmRes.data.filter(i => i.is_urgent).length;
            const now = new Date();
            gmRes.data.forEach(i => {
                if (i.date_entree && i.statut !== 'entree_reservee' && i.statut !== 'sortie_planifiee') {
                    if (Math.ceil((new Date(i.date_entree + 'T00:00') - now) / 86400000) <= 7) { count++; return; }
                }
                if (i.date_sortie && i.statut !== 'sortie_planifiee') {
                    if (Math.ceil((new Date(i.date_sortie + 'T00:00') - now) / 86400000) <= 7) { count++; }
                }
            });
            count += clientsRes.data.filter(c => c.acompte_urgent).length;
            count += (rdvRes.data || []).length;
            setUrgentCount(count);
        } catch (err) { console.error(err); }
    };

    const RELANCE_LABELS = {
        relance_1: { label: 'Relance 1', cls: 'bg-orange-100 text-orange-700' },
        relance_2: { label: 'Relance 2', cls: 'bg-orange-100 text-orange-700' },
        relance_3: { label: 'Relance 3', cls: 'bg-orange-100 text-orange-700' },
        relance_whatsapp: { label: 'WhatsApp', cls: 'bg-green-100 text-green-700' },
        decision_pas_prise: { label: 'Decision pas prise', cls: 'bg-gray-100 text-gray-600' },
        interesse: { label: 'Interesse', cls: 'bg-blue-100 text-blue-700' },
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
                withCredentials: true
            });
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const getRoleLabel = (role) => {
        const labels = {
            admin: 'Administrateur',
            commercial: 'Commercial',
            assistante: 'Assistante',
            responsable: 'Responsable'
        };
        return labels[role] || role;
    };

    // Check if a dashboard block is visible for the current user
    const isBlockVisible = (blockId) => {
        if (user?.role === 'admin') return true;
        const blocks = user?.permissions?.dashboard_blocks;
        if (!blocks) return true; // default: visible if no permissions set
        return blocks[blockId] !== false;
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fadeIn">
                {/* Welcome Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 
                            className="text-2xl sm:text-3xl font-bold text-[#1B365D]"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                            data-testid="dashboard-title"
                        >
                            Bonjour, {user?.name} 👋
                        </h1>
                        <p className="text-gray-500 mt-1">
                            {getRoleLabel(user?.role)} • {new Date().toLocaleDateString('fr-FR', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                            })}
                        </p>
                    </div>
                    <Button 
                        className="hidden sm:inline-flex bg-[#C9A962] hover:bg-[#D4B97A] text-[#1B365D] font-medium"
                        data-testid="add-task-button"
                        onClick={() => setAddTaskOpen(true)}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter une tâche
                    </Button>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Planifier une visite */}
                    {isBlockVisible('bloc_planifier') && (
                    <Card 
                        className="bg-[#1B365D] text-white border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 animate-fadeIn stagger-1 cursor-pointer"
                        data-testid="card-planifier-visite"
                        onClick={() => navigate('/carte-rdv')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-white/70 text-sm font-medium uppercase tracking-wide">
                                        Planifier
                                    </p>
                                    <p className="text-2xl font-bold mt-1 text-[#C9A962]">
                                        Visite
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-[#C9A962]/20 flex items-center justify-center">
                                    <CalendarPlus className="h-6 w-6 text-[#C9A962]" />
                                </div>
                            </div>
                            <Button 
                                variant="ghost" 
                                className="mt-4 w-full text-white hover:bg-white/10 border border-white/20"
                                onClick={() => navigate('/carte-rdv')}
                            >
                                <span>Planifier maintenant</span>
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </CardContent>
                    </Card>
                    )}

                    {/* Tâches à effectuer */}
                    {isBlockVisible('bloc_taches') && (
                    <Card 
                        className="border-t-4 border-t-[#C9A962] shadow-sm hover:shadow-md transition-shadow duration-300 animate-fadeIn stagger-2 cursor-pointer"
                        data-testid="card-taches"
                        onClick={() => navigate('/taches')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Tâches
                                    </p>
                                    <p className="text-4xl font-bold mt-1 text-[#1B365D]">
                                        {canSeeTasks ? tasks.length : stats.taches_en_cours}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
                                    <CheckSquare className="h-6 w-6 text-[#1B365D]" />
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3">À effectuer</p>
                        </CardContent>
                    </Card>
                    )}

                    {/* Urgent */}
                    {isBlockVisible('bloc_problemes') && (
                    <Card 
                        className="border-t-4 border-t-[#EF4444] shadow-sm hover:shadow-md transition-shadow duration-300 animate-fadeIn stagger-3 cursor-pointer"
                        data-testid="card-problemes"
                        onClick={() => navigate('/urgent')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Urgent
                                    </p>
                                    <p className="text-4xl font-bold mt-1 text-[#EF4444]">
                                        {urgentCount}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
                                    <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3">A regler</p>
                        </CardContent>
                    </Card>
                    )}

                    {/* Devis non envoyé */}
                    {isBlockVisible('bloc_devis_non_envoyes') && (
                    <Card 
                        className="border-t-4 border-t-[#10B981] shadow-sm hover:shadow-md transition-shadow duration-300 animate-fadeIn stagger-4 cursor-pointer"
                        data-testid="card-devis-non-envoyes"
                        onClick={() => navigate('/devis-non-envoyes')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Devis non envoyé
                                    </p>
                                    <p className="text-4xl font-bold mt-1 text-[#10B981]">
                                        {devisNonEnvoyesCount}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-green-50 flex items-center justify-center">
                                    <Users className="h-6 w-6 text-[#10B981]" />
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3">Inventaires en attente</p>
                        </CardContent>
                    </Card>
                    )}
                </div>

                {/* Stats Grid - Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Client a relancer */}
                    {isBlockVisible('bloc_clients_relancer') && (
                    <Card
                        className="border-t-4 border-t-orange-400 shadow-sm hover:shadow-md transition-shadow duration-300 cursor-pointer"
                        data-testid="card-clients-relancer-bloc"
                        onClick={() => navigate('/clients-relancer')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Clients a relancer
                                    </p>
                                    <p className="text-4xl font-bold mt-1 text-orange-500">
                                        {clientsRelancer.length}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-orange-50 flex items-center justify-center">
                                    <PhoneCall className="h-6 w-6 text-orange-500" />
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3">A contacter</p>
                        </CardContent>
                    </Card>
                    )}

                    {/* Prospect sans reponse */}
                    {isBlockVisible('bloc_prospects_sans_reponse') && (
                    <Card
                        className="border-t-4 border-t-purple-400 shadow-sm hover:shadow-md transition-shadow duration-300 cursor-pointer"
                        data-testid="card-prospects-sans-reponse-bloc"
                        onClick={() => navigate('/prospects-sans-reponse')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Prospects sans reponse
                                    </p>
                                    <p className="text-4xl font-bold mt-1 text-purple-500">
                                        {prospectsSansReponseCount}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-purple-50 flex items-center justify-center">
                                    <UserX className="h-6 w-6 text-purple-500" />
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3">Sans reponse</p>
                        </CardContent>
                    </Card>
                    )}

                    {/* Bloc Livraison - lien vers la page dediee */}
                    {isBlockVisible('bloc_livraison') && (
                    <Card
                        className="border-t-4 border-t-orange-500 shadow-sm hover:shadow-md transition-shadow duration-300 cursor-pointer"
                        data-testid="card-livraison-bloc"
                        onClick={() => navigate('/livraisons-a-faire')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Livraison
                                    </p>
                                    <p className="text-4xl font-bold mt-1 text-orange-500">
                                        {clientsLivraison.length}
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-orange-50 flex items-center justify-center">
                                    <Truck className="h-6 w-6 text-orange-500" />
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3 flex items-center gap-1">
                                Clients a livrer <ArrowRight className="w-3.5 h-3.5" />
                            </p>
                        </CardContent>
                    </Card>
                    )}

                    {/* Statistiques (acces page dediee, pas dans le menu) */}
                    {isBlockVisible('bloc_statistiques') && (
                    <Card
                        className="border-t-4 border-t-[#0EA5E9] shadow-sm hover:shadow-md transition-shadow duration-300 cursor-pointer"
                        data-testid="card-statistiques-bloc"
                        onClick={() => navigate('/statistiques')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Statistiques
                                    </p>
                                    <p className="text-2xl font-bold mt-1 text-[#0EA5E9]">
                                        Analyses
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-sky-50 flex items-center justify-center">
                                    <BarChart3 className="h-6 w-6 text-[#0EA5E9]" />
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3 flex items-center gap-1">
                                Voir les indicateurs <ArrowRight className="w-3.5 h-3.5" />
                            </p>
                        </CardContent>
                    </Card>
                    )}

                    {/* Ma recherche - carte compacte cliquable */}
                    {isBlockVisible('bloc_recherche') && (
                    <Card
                        className="border-t-4 border-t-indigo-500 shadow-sm hover:shadow-md transition-shadow duration-300 cursor-pointer"
                        data-testid="card-recherche-bloc"
                        onClick={() => navigate('/recherche')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Ma recherche
                                    </p>
                                    <p className="text-2xl font-bold mt-1 text-indigo-500">
                                        Tout
                                    </p>
                                </div>
                                <div className="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center">
                                    <SearchIcon className="h-6 w-6 text-indigo-500" />
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3 flex items-center gap-1">
                                Cherchez partout <ArrowRight className="w-3.5 h-3.5" />
                            </p>
                        </CardContent>
                    </Card>
                    )}

                    {/* Journal d'appel - carte compacte cliquable */}
                    {isBlockVisible('bloc_journal_appels') && (
                    <Card
                        className="border-t-4 border-t-teal-500 shadow-sm hover:shadow-md transition-shadow duration-300 cursor-pointer"
                        data-testid="card-journal-appels-bloc"
                        onClick={() => navigate('/journal-appels')}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-500 text-sm font-medium uppercase tracking-wide">
                                        Journal d'appel
                                    </p>
                                    <p className={`text-4xl font-bold mt-1 ${missedCallsCount > 0 ? 'text-red-500' : 'text-teal-500'}`}>
                                        {missedCallsCount}
                                    </p>
                                </div>
                                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${missedCallsCount > 0 ? 'bg-red-50' : 'bg-teal-50'}`}>
                                    {missedCallsCount > 0
                                        ? <PhoneMissed className="h-6 w-6 text-red-500" />
                                        : <PhoneCall className="h-6 w-6 text-teal-500" />}
                                </div>
                            </div>
                            <p className="text-gray-400 text-sm mt-3 flex items-center gap-1">
                                {missedCallsCount > 0
                                    ? `manqué${missedCallsCount > 1 ? 's' : ''} aujourd'hui`
                                    : "aucun manqué aujourd'hui"}
                                <ArrowRight className="w-3.5 h-3.5" />
                            </p>
                        </CardContent>
                    </Card>
                    )}
                </div>
            </div>

            {/* Mobile FAB - bottom-left */}
            <button
                onClick={() => setAddTaskOpen(true)}
                className="sm:hidden fixed bottom-6 left-6 z-50 h-14 w-14 rounded-full bg-[#C9A962] hover:bg-[#D4B97A] text-[#1B365D] shadow-lg flex items-center justify-center transition-all active:scale-95"
                data-testid="add-task-button-mobile"
                aria-label="Ajouter une tâche"
            >
                <Plus className="w-6 h-6" />
            </button>

            <AddTaskDialog
                open={addTaskOpen}
                onOpenChange={setAddTaskOpen}
                onCreated={() => { if (canSeeTasks) fetchActiveTasks(); }}
            />
        </Layout>
    );
}
